import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2, Eye, EyeOff, User, Lock, ArrowRight, BarChart3, Database, ShieldCheck, Laptop, Mail, KeyRound, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/context/AuthContext";

const Feature = ({ icon: Icon, title, text }) => (
  <div className="flex items-start gap-3.5">
    <div className="w-10 h-10 rounded-xl bg-slate-200/70 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-slate-700" /></div>
    <div><h3 className="text-sm font-semibold text-slate-900">{title}</h3><p className="text-xs text-slate-500">{text}</p></div>
  </div>
);

const Login = () => {
  const [mode, setMode] = useState("otp");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, requestOtp, verifyOtp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const interval = window.setInterval(() => setResendIn((seconds) => seconds - 1), 1000);
    return () => window.clearInterval(interval);
  }, [resendIn]);

  const displayError = (err, fallback) => {
    const detail = err.response?.data?.detail;
    if (typeof detail === "string" && detail.trim()) {
      setError(detail);
      return;
    }
    if (Array.isArray(detail)) {
      const messages = detail
        .map((item) => (typeof item === "string" ? item : item?.msg))
        .filter(Boolean);
      setError(messages.length ? messages.join("; ") : fallback);
      return;
    }
    if (detail && typeof detail === "object") {
      setError(detail.msg || detail.message || fallback);
      return;
    }
    setError(fallback);
  };

  const selectMode = (nextMode) => {
    setMode(nextMode);
    setError("");
    setMessage("");
    setOtp("");
    setOtpSent(false);
  };

  const handleAdminSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(username, password);
      navigate(from, { replace: true });
    } catch (err) {
      console.error("Administrator login failed:", err);
      displayError(err, "Invalid username or password");
    } finally {
      setLoading(false);
    }
  };

  const sendOtp = async (event) => {
    event?.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);
    try {
      const response = await requestOtp(email);
      setOtpSent(true);
      setOtp("");
      setResendIn(60);
      setMessage(response.message || "If an active account exists for this email, a sign-in code has been sent.");
    } catch (err) {
      console.error("OTP request failed:", err);
      displayError(err, "We could not send a sign-in code. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      await verifyOtp(email, otp);
      navigate(from, { replace: true });
    } catch (err) {
      console.error("OTP verification failed:", err);
      displayError(err, "Invalid or expired sign-in code");
    } finally {
      setLoading(false);
    }
  };

  const useDifferentEmail = () => {
    setOtpSent(false);
    setOtp("");
    setError("");
    setMessage("");
    setResendIn(0);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-3 sm:p-5 lg:p-6" data-testid="login-page">
      <div className="w-full max-w-4xl lg:max-w-5xl grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-center bg-slate-50/50 rounded-2xl p-2 sm:p-4">
        <div className="lg:col-span-6 space-y-6 px-2 sm:px-3">
          <div>
            <img src="/assets/mesprosoft-logo.png" alt="MESPROSOFT" className="h-13 sm:h-16 w-auto mb-6" />
            <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight">One Platform.<br />All SAP Data.</h1>
            <div className="w-14 h-1 bg-slate-300 rounded mt-3 mb-4" />
            <p className="text-slate-600 text-sm sm:text-base max-w-md">Access, analyze, and act on your SAP data across systems in one unified platform.</p>
          </div>
          <div className="space-y-4 pt-1">
            <Feature icon={Database} title="Unified SAP Data" text="Connect and view data from all your SAP systems in one place." />
            <Feature icon={BarChart3} title="Real-time Insights" text="Get real-time dashboards and actionable insights." />
            <Feature icon={ShieldCheck} title="Secure & Reliable" text="Enterprise-grade security with role-based access and data protection." />
            <Feature icon={Laptop} title="Cross Platform Access" text="Access anytime, anywhere on any device you prefer." />
          </div>
          <p className="text-xs text-slate-400 pt-2">&copy; 2026 MESPROSOFT. All rights reserved.</p>
        </div>

        <div className="lg:col-span-6 flex justify-center lg:justify-end">
          <div className="w-full max-w-sm bg-white rounded-2xl p-6 sm:p-7 shadow-lg border border-slate-100 relative">
            <div className="w-13 h-13 rounded-full bg-slate-100 mx-auto flex items-center justify-center mb-3 p-3">
              {mode === "otp" ? <Mail className="w-6 h-6 text-slate-600" /> : <KeyRound className="w-6 h-6 text-slate-600" />}
            </div>
            <h2 className="text-xl font-bold text-slate-900 text-center">{mode === "otp" ? "Welcome Back!" : "Administrator Sign In"}</h2>
            <p className="text-[11px] text-slate-500 text-center mt-0.5 mb-4">{mode === "otp" ? "Sign in securely with a one-time email code" : "Use your administrator username and password"}</p>

            <div className="grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1 mb-5" role="tablist" aria-label="Sign-in method">
              <button type="button" role="tab" aria-selected={mode === "otp"} onClick={() => selectMode("otp")} className={`rounded-md px-3 py-2 text-xs font-medium transition-colors ${mode === "otp" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>User OTP</button>
              <button type="button" role="tab" aria-selected={mode === "admin"} onClick={() => selectMode("admin")} className={`rounded-md px-3 py-2 text-xs font-medium transition-colors ${mode === "admin" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>Administrator</button>
            </div>

            {error && <Alert variant="destructive" data-testid="login-error" className="mb-4 py-2 text-xs"><AlertDescription>{error}</AlertDescription></Alert>}
            {message && <Alert className="mb-4 border-slate-200 bg-slate-50 py-2 text-xs text-slate-700"><AlertDescription>{message}</AlertDescription></Alert>}

            {mode === "admin" ? (
              <form onSubmit={handleAdminSubmit} className="space-y-3.5">
                <div className="space-y-1"><Label htmlFor="username" className="text-[11px] font-semibold text-slate-700">Username</Label><div className="relative"><User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input id="username" type="text" placeholder="Enter your username" value={username} onChange={(event) => setUsername(event.target.value)} required autoComplete="username" className="pl-9 h-9.5 text-xs rounded-lg border-slate-200 focus:border-slate-400" data-testid="login-username" /></div></div>
                <div className="space-y-1"><Label htmlFor="password" className="text-[11px] font-semibold text-slate-700">Password</Label><div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input id="password" type={showPassword ? "text" : "password"} placeholder="Enter your password" value={password} onChange={(event) => setPassword(event.target.value)} required autoComplete="current-password" className="pl-9 pr-9 h-9.5 text-xs rounded-lg border-slate-200 focus:border-slate-400" data-testid="login-password" /><button type="button" onClick={() => setShowPassword((visible) => !visible)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors" aria-label={showPassword ? "Hide password" : "Show password"}>{showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button></div></div>
                <Button type="submit" className="w-full h-10 rounded-lg bg-slate-800 hover:bg-slate-900 text-white text-xs font-medium flex items-center justify-center gap-2 mt-3 shadow-sm" disabled={loading} data-testid="login-submit">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ArrowRight className="w-4 h-4" /><span>Sign In</span></>}</Button>
              </form>
            ) : !otpSent ? (
              <form onSubmit={sendOtp} className="space-y-3.5">
                <div className="space-y-1"><Label htmlFor="email" className="text-[11px] font-semibold text-slate-700">Work email</Label><div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input id="email" type="email" placeholder="name@company.com" value={email} onChange={(event) => setEmail(event.target.value)} required autoComplete="email" className="pl-9 h-9.5 text-xs rounded-lg border-slate-200 focus:border-slate-400" data-testid="login-email" /></div></div>
                <Button type="submit" className="w-full h-10 rounded-lg bg-slate-800 hover:bg-slate-900 text-white text-xs font-medium flex items-center justify-center gap-2 mt-3 shadow-sm" disabled={loading} data-testid="request-otp-submit">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4" /><span>Send Sign-In Code</span></>}</Button>
              </form>
            ) : (
              <form onSubmit={verifyCode} className="space-y-3.5">
                <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600"><span className="font-medium text-slate-800">Code sent to:</span> {email}</div>
                <div className="space-y-1"><Label htmlFor="otp" className="text-[11px] font-semibold text-slate-700">Six-digit sign-in code</Label><div className="relative"><KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input id="otp" type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} placeholder="123456" value={otp} onChange={(event) => setOtp(event.target.value.replace(/\D/g, "").slice(0, 6))} required autoComplete="one-time-code" className="pl-9 h-10 text-center text-base tracking-[0.35em] rounded-lg border-slate-200 focus:border-slate-400" data-testid="login-otp" /></div><p className="text-[11px] text-slate-500">The code expires in 5 minutes.</p></div>
                <Button type="submit" className="w-full h-10 rounded-lg bg-slate-800 hover:bg-slate-900 text-white text-xs font-medium flex items-center justify-center gap-2 mt-3 shadow-sm" disabled={loading || otp.length !== 6} data-testid="verify-otp-submit">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ArrowRight className="w-4 h-4" /><span>Verify & Sign In</span></>}</Button>
                <div className="flex items-center justify-between text-[11px]"><button type="button" onClick={useDifferentEmail} className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-800"><ArrowLeft className="h-3 w-3" />Use another email</button><button type="button" onClick={sendOtp} disabled={loading || resendIn > 0} className="font-medium text-slate-700 hover:underline disabled:text-slate-400 disabled:no-underline">{resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}</button></div>
              </form>
            )}

            <div className="mt-6 pt-3 border-t border-slate-100 text-center"><p className="text-[11px] text-slate-500">Need help? <a href="mailto:support@mesprosoft.com" className="font-semibold text-slate-800 hover:underline transition-colors">Contact Support</a></p></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
